import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { BookingApi } from './api';

/**
 * What the client booking journey needs from outside React.
 *
 * One api, and only one: the menu, the add-ons, the technicians, the slots and the
 * booking all come from BP-04's own routes. The catalogue's and the roster's *staff*
 * routes need a staff session, and §6 puts the OTP step after the basket — so the client
 * journey reads the `/public/*` surface instead, which answers a visitor with exactly
 * what a booking page prints.
 *
 * There is **no** ability flag here. The client app has one kind of caller, and everything
 * it may do the api decides (R14): a client may read and change their own bookings and
 * nothing else, whatever this bundle says.
 */
export interface BookingDeps {
  api: BookingApi;
  /** A fresh `Idempotency-Key` per confirm (ARCHITECTURE §3 API). */
  newIdempotencyKey: () => string;
  /** The tenant's IANA timezone, for the dates and times the journey shows. */
  timezone: string;
  /** Where the DPDP notice lives, for the link beside the consent tick (BP-03 §6). */
  noticeHref: string;
}

const BookingContext = createContext<BookingDeps | null>(null);

export function BookingProvider({
  deps,
  children,
}: {
  deps: BookingDeps;
  children: ReactNode;
}): JSX.Element {
  return <BookingContext.Provider value={deps}>{children}</BookingContext.Provider>;
}

/** The booking dependencies. Throws when a screen is rendered outside the provider. */
export function useBookingDeps(): BookingDeps {
  const deps = useContext(BookingContext);
  if (deps === null) {
    throw new Error('26AD booking: this screen must be rendered inside <BookingProvider>');
  }
  return deps;
}
